import { Box } from '@mui/material';
import { alpha } from '@mui/material/styles';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef } from 'react';
import { ChatPanelMaxWidth, ChatPanelMinWidth } from '../chatConfig';

const KeyboardStep = 16;
const KeyboardStepLarge = 64;

ChatResizeHandle.propTypes = {
	width: PropTypes.number.isRequired,
	onResize: PropTypes.func.isRequired,
	onResizeStart: PropTypes.func.isRequired,
	onResizeEnd: PropTypes.func.isRequired,
};

// Mouse events rather than pointer events: the handle is desktop-only, and
// this keeps it working in the jsdom the tests run in.
export default function ChatResizeHandle({
	width,
	onResize,
	onResizeStart,
	onResizeEnd,
}) {
	// Where the gesture started, so the window listeners need no rebinding.
	const dragRef = useRef(null);

	const stopDrag = useCallback(() => {
		if (!dragRef.current) {
			return;
		}
		dragRef.current = null;
		document.body.style.cursor = '';
		document.body.style.userSelect = '';
		onResizeEnd();
	}, [onResizeEnd]);

	useEffect(() => {
		const onMouseMove = (event) => {
			if (!dragRef.current) {
				return;
			}
			// The handle is on the left edge, so dragging left widens the panel.
			onResize(dragRef.current.width + (dragRef.current.x - event.clientX));
		};

		window.addEventListener('mousemove', onMouseMove);
		window.addEventListener('mouseup', stopDrag);
		return () => {
			window.removeEventListener('mousemove', onMouseMove);
			window.removeEventListener('mouseup', stopDrag);
			// Otherwise the overridden cursor outlives the component.
			stopDrag();
		};
	}, [onResize, stopDrag]);

	const onMouseDown = (event) => {
		// Otherwise the drag selects the text it passes over.
		event.preventDefault();
		dragRef.current = { x: event.clientX, width: width };
		document.body.style.cursor = 'col-resize';
		document.body.style.userSelect = 'none';
		onResizeStart();
	};

	const onKeyDown = (event) => {
		const step = event.shiftKey ? KeyboardStepLarge : KeyboardStep;

		if (event.key === 'ArrowLeft') {
			event.preventDefault();
			onResize(width + step);
		} else if (event.key === 'ArrowRight') {
			event.preventDefault();
			onResize(width - step);
		}
	};

	return (
		<Box
			role="separator"
			aria-orientation="vertical"
			aria-label="Resize assistant panel"
			aria-valuenow={width}
			aria-valuemin={ChatPanelMinWidth}
			aria-valuemax={ChatPanelMaxWidth}
			tabIndex={0}
			onMouseDown={onMouseDown}
			onKeyDown={onKeyDown}
			sx={{
				flexShrink: 0,
				width: 6,
				cursor: 'col-resize',
				borderRight: '1px solid',
				borderColor: 'divider',
				backgroundColor: 'transparent',
				transition: 'background-color 150ms',
				'&:hover, &:focus-visible': {
					backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.4),
				},
				'&:focus-visible': { outline: 'none' },
			}}
		/>
	);
}
